/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Table } from 'antd';
import { FaEye, FaEyeSlash, FaPen, FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import usePaginationHook from '../../../../utils/usePagination';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertConfirm } from '../../../util/Alert';
import { FormProvider, useForm } from 'react-hook-form';
import 'dayjs/locale/th';
import { useHistory } from 'react-router';
import fitSurveyServices from '../../../../services/fit-survey.services';
import {
  legacySurveyQuestionFormDefaultValues,
  legacySurveyQuestionFormSchema,
} from '../components/dto/legacy-survey-form.dto';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import LegacySurveyQuestionModal from '../components/modal/LegacySurveyQuestionModal';
import { FiArrowLeft } from 'react-icons/fi';

dayjs.locale('th');

const pageSize = 10;

const LegacySurveyQuestionPage = () => {
  const [legacySurveyQuestionFormOpen, setLegacySurveyQuestionFormOpen] =
    useState(false);
  const {
    data: legacySurveyQuestionLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();
  const history = useHistory();
  const params = useParams();

  const legacySurveyQuestionForm = useForm({
    defaultValues: legacySurveyQuestionFormDefaultValues,
    resolver: legacySurveyQuestionFormSchema(),
  });

  const _HandleLegacySurveyQuestionFormOpen = (formType) => {
    legacySurveyQuestionForm.setValue('formType', formType);
    setLegacySurveyQuestionFormOpen(true);
  };

  const _HandleLegacySurveyQuestionFormClose = () => {
    legacySurveyQuestionForm.reset(legacySurveyQuestionFormDefaultValues);
    setLegacySurveyQuestionFormOpen(false);
  };

  fitSurveyServices.useQueryGetLegacySurveyQuestionLists(
    { take: pageSize, page, surveyId: params.surveyId },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    }
  );

  const { mutate: deleteLegacySurveyQuestion } =
    fitSurveyServices.useMutationDeleteLegacySurveyQuestion(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-legacy-survey-question'],
        refetchType: 'all',
      });
    });

  const { mutate: updateLegacySurveyQuestionDisplay } =
    fitSurveyServices.useMutationUpdateLegacySurveyQuestionDisplay(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-legacy-survey-question'],
        refetchType: 'all',
      });
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'รูปภาพ',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (_, record) => (
        <div>
          {record.imageUrl && (
            <img
              alt={record.question}
              src={record.imageUrl}
              style={{
                width: 100,
                height: 100,
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'คำถาม',
      dataIndex: 'question',
      key: 'question',
      render: (_, record) => <span>{record.question}</span>,
      width: 300,
    },
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      render: (_, record) => (
        <span>
          {record.type === 'select' && 'เลือกคำตอบ'}
          {record.type === 'text' && 'บรรยายคำตอบ'}
        </span>
      ),
    },
    {
      title: 'การมองเห็น',
      dataIndex: 'display',
      key: 'display',
      render: (_, record) => <span>{record.display ? 'เปิด' : 'ปิด'}</span>,
    },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>{dayjs(record.updatedAt).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.updatedAt).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {record.display ? (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('ปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyQuestionDisplay({
                    id: record.id,
                    display: false,
                  });
              }}
            >
              <FaEye />
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('เปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyQuestionDisplay({
                    id: record.id,
                    display: true,
                  });
              }}
            >
              <FaEyeSlash />
            </div>
          )}
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              legacySurveyQuestionForm.setValue('id', record.id);
              _HandleLegacySurveyQuestionFormOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteLegacySurveyQuestion({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h3
          onClick={() => {
            history.goBack();
          }}
          className="mr-2"
          style={{ cursor: 'pointer' }}
        >
          <FiArrowLeft />
        </h3>
      </div>
      <Panel>
        <PanelHeader>คำถาม</PanelHeader>
        <PanelBody>
          <div
            style={{ display: 'flex', alignItems: 'center' }}
            className="mb-2"
          >
            <div style={{ flex: 1 }} />
            <Button
              onClick={() => {
                _HandleLegacySurveyQuestionFormOpen('create');
              }}
              type="primary"
            >
              <FaPlus className="mr-2" />
              เพิ่มคำถาม
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={legacySurveyQuestionLists}
            style={{ backgroundColor: '#fff' }}
            rowKey="id"
            onChange={({ current }) => {
              setPage(current);
            }}
            pagination={{
              pageSize: 10,
              total,
            }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  history.push(
                    `/questionaire/survey/${params.surveyId}/${record.type}/${record.id}`
                  );
                },
              };
            }}
          />
        </PanelBody>
        <FormProvider {...legacySurveyQuestionForm}>
          <LegacySurveyQuestionModal
            open={legacySurveyQuestionFormOpen}
            handleClose={_HandleLegacySurveyQuestionFormClose}
          />
        </FormProvider>
      </Panel>
    </>
  );
};

export default LegacySurveyQuestionPage;
